/**
 * theme.js — design tokens as CSS custom properties.
 *
 * WHY THIS REPLACES THE `c` PROP
 *
 * The original threaded a colour object through every component
 * (`<ArticleCard c={c} …>`, `<Eyebrow c={c}>`, `<SignalMeter c={c} forceLight>`).
 * Three consequences, all of which showed up as real bugs:
 *
 *   1. Any component that forgot the prop silently rendered light-mode colours
 *      on a dark background — that is exactly why the relevance prompt and
 *      several dropdowns were invisible in dark mode.
 *   2. `forceLight` had to be plumbed through as a second colour system, so
 *      some components had two hard-coded palettes.
 *   3. Every colour change re-rendered the entire tree.
 *
 * CSS variables solve all three: they cascade, they cost nothing to change,
 * and a component physically cannot forget to receive them.
 *
 * CONTRAST
 * Every foreground/background pair below is checked to WCAG 2.1 AA
 * (4.5:1 body text, 3:1 large text and UI boundaries). The measured ratio is
 * noted where it is close, so a future tweak cannot quietly break it.
 */

export const THEME_CSS = `
:root {
  color-scheme: light;

  /* Surfaces */
  --bg:          #F4F5F0;
  --bg-alt:      #EAEBE4;
  --card:        #FFFFFF;
  --card-hover:  #F7F8F4;
  --line:        #D5D8CC;   /* dividers only: 1.4:1, decorative */
  --line-strong: #B9BDAE;   /* card borders: a light but visible edge */
  --line-ui:     #7F867A;   /* control boundaries: 3.7:1 on --card, 3.1:1 on --bg-alt (WCAG 1.4.11) */

  /* Text */
  --ink:         #14201C;   /* 15.8:1 on --bg */
  --ink-soft:    #4A554E;   /*  7.4:1 on --bg */
  --ink-faint:   #5E6962;   /*  5.2:1 on --bg, 4.8:1 on --bg-alt — was 4.2:1 there */
  --on-accent:   #FFFFFF;
  /* Text on a filled status colour (danger, success, warning). White in light
     mode; near-black in dark, where the status colours are light. White on
     them was 2.0–2.4:1 in dark mode. */
  --on-status:   #FFFFFF;

  /* Brand */
  --primary:        #1A5449;   /* 8.6:1 with white text */
  --primary-hover:  #144039;
  --primary-tint:   #E7F0EB;
  --primary-text:   #14453C;   /* for primary-coloured text on light surfaces */

  /* Status */
  --success:      #2F7D4F;
  /* A green chip that stays green whatever accent is chosen ("High
     confidence"): ink 6.1:1 on its tint. */
  --success-bg:   #E6F4EA;
  --success-ink:  #23663F;
  --warning:      #8A5A12;   /* darkened from #C98A2C, which was 2.8:1 and failed */
  --warning-bg:   #FDF4E3;
  --danger:       #96402F;   /* 5.2:1 on --card */
  --danger-bg:    #FBEDEA;
  --info:         #2C5C91;

  /* Relevance meter */
  --score-high:   #1A5449;
  --score-mid:    #8A5A12;
  --score-low:    #96402F;

  /* The NEW flag. Gold rather than the accent colour, deliberately: the accent
     changes with the user's theme choice, and a recency marker that turns plum
     or slate stops reading as "new". Contrast checked at AA against --new-bg. */
  /* World news. Fixed blue, independent of the accent: the border's job is
     to mark these as not-your-briefing, and a marker that changes colour with
     the theme cannot carry a fixed meaning. */
  --news-line:    #2C5C91;
  --news-tint:    #E4EDF7;
  --news-ink:     #1F4470;

  --new-bg:       #FBF0D2;
  --new-line:     #E3C878;
  --new-ink:      #6B4E0A;

  /* Focus — must be visible on every surface, hence the double ring */
  --focus:        #2C5C91;
  --focus-ring:   0 0 0 2px var(--card), 0 0 0 4px var(--focus);

  /* Elevation */
  --shadow-sm:  0 1px 2px rgba(20, 32, 28, 0.06);
  --shadow-md:  0 4px 12px rgba(20, 32, 28, 0.10);
  --shadow-lg:  0 12px 32px rgba(20, 32, 28, 0.16);

  /* Type */
  --font-display: 'Fraunces', Georgia, 'Times New Roman', serif;
  --font-body:    'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  --font-mono:    'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace;

  /* Minimum readable sizes. The original used 9-11px for metadata, which is
     below the practical floor for a tired clinician on a night shift. */
  --text-xs:   0.75rem;   /* 12px — the smallest text anywhere in the app */
  --text-sm:   0.8125rem; /* 13px */
  --text-base: 0.9375rem; /* 15px */
  --text-lg:   1.0625rem; /* 17px */
  --text-xl:   1.25rem;
  --text-2xl:  1.5rem;

  /* Layout */
  --radius-sm: 8px;
  --radius:    12px;
  --radius-lg: 16px;
  --radius-full: 999px;
  --tap:       44px;      /* WCAG 2.5.5 minimum target size */
  --header-h:  56px;
  --content-w: 640px;

  --transition: 160ms cubic-bezier(0.4, 0, 0.2, 1);

  /* z-index scale — every layered element pulls from here.
     The original hard-coded 20, 30, 40, 50, 100, 200, 500, 999 and 9999 across
     different files, which is precisely why dropdowns rendered underneath the
     sticky header and behind cards. */
  --z-base:     0;
  --z-sticky:   100;
  --z-header:   200;
  --z-dropdown: 400;
  --z-overlay:  500;
  --z-modal:    600;
  --z-toast:    700;
}

[data-theme='dark'] {
  color-scheme: dark;

  --bg:          #0F1512;
  --bg-alt:      #171E1A;
  --card:        #1A221E;
  --card-hover:  #202A25;
  --line:        #303A34;
  --line-strong: #46524A;
  --line-ui:     #6E7B72;   /* 3.7:1 on --card, 3.8:1 on --bg-alt */

  --ink:         #E8EBE6;   /* 14.2:1 on --bg */
  --ink-soft:    #A3B0A7;   /*  8.1:1 on --bg */
  --ink-faint:   #8A9790;   /*  5.9:1 on --bg */
  --on-accent:   #0F1512;
  --on-status:   #0F1512;

  --primary:        #5FB3A1;   /* light enough to carry dark text at 8.0:1 */
  --primary-hover:  #74C4B3;
  --primary-tint:   #172C26;
  --primary-text:   #7FCBB9;

  --success:      #6FC08C;
  --success-bg:   #15281C;   /* ink 8.1:1 */
  --success-ink:  #7FCC9A;
  --warning:      #E0B15C;
  --warning-bg:   #2A2312;
  --danger:       #E39182;
  --danger-bg:    #2C1B17;
  --info:         #8FB6E4;

  --score-high:   #5FB3A1;
  --score-mid:    #E0B15C;
  --score-low:    #E39182;

  --news-line:    #6FA3DC;
  --news-tint:    #16273A;
  --news-ink:     #9CC4EC;

  --new-bg:       #3A2F10;
  --new-line:     #6E5A20;
  --new-ink:      #F0CE78;

  --focus:        #8FB6E4;
  --focus-ring:   0 0 0 2px var(--card), 0 0 0 4px var(--focus);

  --shadow-sm:  0 1px 2px rgba(0, 0, 0, 0.4);
  --shadow-md:  0 4px 14px rgba(0, 0, 0, 0.5);
  --shadow-lg:  0 12px 36px rgba(0, 0, 0, 0.6);
}

/* Windows High Contrast / forced-colors mode. Without this, everything is
   drawn with background colours the OS then overrides, and the UI vanishes. */
@media (forced-colors: active) {
  :root {
    --line: CanvasText;
    --line-strong: CanvasText;
    --line-ui: CanvasText;
    --focus: Highlight;
  }
  .btn, .card, .menu { border: 1px solid CanvasText; }
}

/* ── Base ─────────────────────────────────────────────────────────────────── */

*, *::before, *::after { box-sizing: border-box; }

html { -webkit-text-size-adjust: 100%; }

body {
  margin: 0;
  background: var(--bg);
  color: var(--ink);
  font-family: var(--font-body);
  font-size: var(--text-base);
  line-height: 1.55;
  -webkit-font-smoothing: antialiased;
  /* Stops iOS Safari zooming the whole page when an input is focused. */
  text-size-adjust: 100%;
}

/**
 * Inputs below 16px make iOS Safari zoom the whole page on focus.
 *
 * !important is load-bearing, and this is one of the few places it is the
 * right tool rather than a shortcut. The rule was written with bare element
 * selectors — specificity (0,0,1) — so EVERY class that sets a font-size on a
 * field beat it: .thread__input at 13px, .field__input at 15px,
 * .comment-composer__input, .composer__input. The guard existed, applied to
 * nothing, and every form on the site zoomed on an iPhone. Typing a message
 * meant zooming out again afterwards, on every single message.
 *
 * Raising the specificity by listing each class would work until somebody adds
 * the next input and does not know to add it here. This is a hard usability
 * floor, not a style preference: on a touch device, no field is under 16px.
 */
@media (pointer: coarse) {
  input, select, textarea { font-size: 16px !important; }

  /**
   * The touch target floor, WCAG 2.5.5.
   *
   * --tap: 44px is defined above and applied to fields and buttons, but the
   * feed's own actions — react, comment, repost, save — were built at 38px,
   * and those are the four controls a reader actually uses. Four pixels short
   * is the difference between hitting Repost and hitting Save.
   *
   * Only on coarse pointers: a mouse is precise and 38px looks better on a
   * desktop feed, so this is not a change to the design, it is a change to the
   * design on the device that needs it.
   */
  .post__action,
  .comment__action,
  .acard__source { min-height: var(--tap); }

  /**
   * Small icon buttons keep their size and grow their hit area instead.
   *
   * Making a 28px dismiss button physically 44px would push the notification
   * text out of the row. An invisible pseudo-element extends the touchable
   * region without moving anything, which is the same trick .toggle and
   * .colour-dot already use here.
   */
  .notif__dismiss::before,
  .composer__preview-remove::before,
  .pollbuild__remove::before {
    content: ''; position: absolute; inset: -8px;
  }
}

/* Focus is visible for keyboard users and invisible for mouse users, which is
   the behaviour people expect. :focus-visible does this natively. */
:focus { outline: none; }
:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring);
  border-radius: var(--radius-sm);
}

/* Respect the OS "reduce motion" setting. Vestibular disorders are common, and
   the original ran unconditional scale/rotate/translate animations. */
@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }
}

/* Screen-reader-only text that is still focusable (for skip links). */
.sr-only {
  position: absolute;
  width: 1px; height: 1px;
  padding: 0; margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
.sr-only-focusable:focus,
.sr-only-focusable:focus-visible {
  position: fixed;
  top: 8px; left: 8px;
  width: auto; height: auto;
  padding: 12px 18px;
  margin: 0;
  clip: auto;
  background: var(--card);
  color: var(--ink);
  border-radius: var(--radius);
  box-shadow: var(--shadow-lg);
  z-index: var(--z-toast);
}

/* Long clinical terms and pasted URLs must not blow out a card's width. */
.wrap-anywhere { overflow-wrap: anywhere; word-break: break-word; }

/**
 * Every container that renders text a USER wrote.
 *
 * A .wrap-anywhere utility only helps where somebody remembered to apply it,
 * and the feed is exactly where it was forgotten: .post__text had
 * white-space: pre-wrap and no wrapping rule at all, so one pasted DOI or a
 * long chemical name pushed the text straight off the side of the screen.
 *
 * Listed centrally rather than per-component so the next text surface someone
 * adds is covered by default. overflow-wrap: anywhere breaks only when a word
 * genuinely cannot fit, so ordinary prose is untouched.
 */
.post__text,
.comment__text,
.bubble,
.quoted__text,
.notif__preview,
.conv-row__preview,
.rep__quote,
.report__quote,
.rich-text {
  overflow-wrap: anywhere;
}

/* ── Animations ───────────────────────────────────────────────────────────── */

@keyframes fade-in-up { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
@keyframes pop-in     { from { opacity: 0; transform: scale(0.96); }    to { opacity: 1; transform: none; } }
@keyframes slide-up   { from { transform: translateY(100%); }           to { transform: none; } }
@keyframes spin       { to { transform: rotate(360deg); } }
@keyframes heart-pop  { 0% { transform: scale(1); } 40% { transform: scale(1.35); } 70% { transform: scale(0.92); } 100% { transform: scale(1); } }
@keyframes shimmer    { 0% { background-position: -400px 0; } 100% { background-position: 400px 0; } }
@keyframes ring-draw  { to { stroke-dashoffset: 0; } }

.anim-fade-in { animation: fade-in-up var(--transition) both; }
.anim-pop     { animation: pop-in 180ms cubic-bezier(0.34, 1.4, 0.64, 1) both; }
.anim-heart   { animation: heart-pop 340ms ease both; }

/* ── Skeleton loaders ─────────────────────────────────────────────────────── */

.skeleton {
  background: linear-gradient(
    90deg,
    var(--bg-alt) 25%,
    var(--card-hover) 50%,
    var(--bg-alt) 75%
  );
  background-size: 800px 100%;
  animation: shimmer 1.4s linear infinite;
  border-radius: var(--radius-sm);
}
@media (prefers-reduced-motion: reduce) {
  .skeleton { animation: none; background: var(--bg-alt); }
}
`;

/* ══════════════════════════════════════════════════════════════════════════
   ACCENT COLOURS
   ══════════════════════════════════════════════════════════════════════════

   Each accent supplies four values per mode, because a single hue cannot do
   the work of four:

     primary       fills — buttons, the tick, the score meter
     primaryHover  the same, one step darker (light) or lighter (dark)
     primaryText   the accent as TEXT on a page background. Always darker than
                   the fill in light mode; a colour that reads well as a button
                   fill is usually 3:1 or worse as body text, which fails WCAG.
     primaryTint   a wash for selected chips and highlight rows

   Every light-mode `primary` clears 4.5:1 against white text, and every
   `primaryText` clears 4.5:1 against the page background. That is checked, not
   assumed — an accent picker is exactly the feature that quietly destroys a
   carefully built contrast system, because it lets colour be chosen for
   prettiness by someone who will never see the contrast report.
   ────────────────────────────────────────────────────────────────────────── */

export const ACCENTS = {
  forest: {
    label: 'Forest',
    light: { primary: '#1A5449', hover: '#144039', text: '#14453C', tint: '#E7F0EB' },
    dark:  { primary: '#5FB3A1', hover: '#74C4B3', text: '#7FCBB9', tint: '#172C26' },
  },
  slate: {
    label: 'Slate',
    light: { primary: '#2C4A63', hover: '#20374A', text: '#1F3D53', tint: '#E6ECF2' },
    dark:  { primary: '#7FAECF', hover: '#96BEDA', text: '#9CC3DE', tint: '#16242F' },
  },
  plum: {
    label: 'Plum',
    light: { primary: '#5A3364', hover: '#45274D', text: '#4B2A54', tint: '#F0E8F2' },
    dark:  { primary: '#C193CE', hover: '#D0A8DB', text: '#CFA6D9', tint: '#261A2A' },
  },
  clay: {
    label: 'Clay',
    light: { primary: '#8A4230', hover: '#6E3325', text: '#7A3A2A', tint: '#F7EAE6' },
    dark:  { primary: '#D9917D', hover: '#E4A794', text: '#E0A08D', tint: '#2E1C17' },
  },
  ink: {
    label: 'Ink',
    light: { primary: '#26303A', hover: '#171F26', text: '#20282F', tint: '#E8EAEC' },
    dark:  { primary: '#A8B6C2', hover: '#BCC8D2', text: '#B6C3CE', tint: '#1B2229' },
  },
  /**
   * Yellow, as far as yellow can honestly go.
   *
   * A true yellow (#FFD400 and anything near it) is the one hue that cannot be
   * an accent here, because --on-accent is white in light mode and white on
   * yellow measures about 1.3:1 — illegible, not merely poor. So light mode
   * uses the deepest gold that still reads as yellow while carrying white text
   * at 5.4:1, and dark mode, where --on-accent is near-black, gets the bright
   * yellow the name promises at 11:1.
   *
   * That asymmetry is the point: the same accent has to work on both, and only
   * the dark background can afford a light fill.
   */
  amber: {
    label: 'Amber',
    light: { primary: '#8A6410', hover: '#6E4F0A', text: '#6B4E0A', tint: '#FBF0D2' },
    dark:  { primary: '#E8C46A', hover: '#F0D183', text: '#EFD189', tint: '#2E2611' },
  },
};

export const DEFAULT_ACCENT = 'forest';

// Storage keys keep the 'leeflet.' prefix from the service's previous name on
// purpose: renaming them would reset every member's theme, cookie choice,
// unread dots and saved article drafts. Nobody sees them.
const THEME_KEY = 'leeflet.theme';
const ACCENT_KEY = 'leeflet.accent';

/**
 * THE PRE-PAINT BOOTSTRAP LIVES IN index.html, NOT HERE.
 *
 * There used to be a `THEME_BOOTSTRAP` export at this point holding a copy of
 * that script. Nothing ever imported it. It could not be imported — the whole
 * point of the bootstrap is that it runs before the bundle exists — so it sat
 * here looking authoritative while the code that actually ran was the hand-copy
 * in index.html.
 *
 * Which is exactly the trap you would predict: when the amber accent was added
 * it went into the copy here and not into the one that runs, so amber users got
 * a flash of forest on every load and nothing in this file hinted why.
 *
 * One copy now, in index.html. If you add an accent to ACCENTS above, add it to
 * the `A` table in index.html as well. The localStorage keys it reads are
 * `leeflet.theme` and `leeflet.accent` — THEME_KEY and ACCENT_KEY above.
 */

function resolveMode(pref) {
  if (pref === 'light' || pref === 'dark') return pref;
  // 'system', or anything unrecognised, follows the OS.
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/* ── Change notification ───────────────────────────────────────────────────── */

/**
 * One source of truth, broadcast to whoever is rendering it.
 *
 * The header's sun/moon button and Settings → Appearance both used to read the
 * current theme ONCE into their own useState and then never hear about it
 * again. So flipping the header toggle left the Settings radio showing the old
 * choice, and choosing in Settings left the header showing the wrong icon —
 * two controls for one setting, disagreeing with each other and with the page
 * they were sitting on.
 *
 * Deliberately a plain Set rather than React context: applyTheme is called from
 * the profile listener and from the pre-React bootstrap path too, neither of
 * which can dispatch into a provider.
 */
const listeners = new Set();

export function onThemeChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function emit() {
  const state = {
    pref: currentThemePref(),
    mode: currentTheme(),
    accent: currentAccent(),
  };
  // One bad subscriber must not stop the others being told.
  for (const fn of [...listeners]) {
    try { fn(state); } catch { /* ignore */ }
  }
}

/**
 * Paint the chosen accent onto the document as inline custom properties.
 *
 * The mode defaults to the DOM attribute, NOT to resolveMode(currentThemePref()).
 * That distinction is the fix for a whole class of bug: data-theme is what the
 * CSS is actually obeying, whereas the stored preference is what we intend it
 * to be, and for a moment during sign-in — while a second account's preference
 * is being applied — the two disagree. Reading the preference in that window
 * paints dark-mode accent values onto a light page, which is how you get a
 * callout with a near-black wash behind near-black text.
 *
 * Taking the mode from the element the browser is styling makes the two
 * physically unable to drift.
 */
export function applyAccent(accentKey, mode = currentTheme()) {
  const accent = ACCENTS[accentKey] ?? ACCENTS[DEFAULT_ACCENT];
  const c = mode === 'dark' ? accent.dark : accent.light;
  const s = document.documentElement.style;

  s.setProperty('--primary', c.primary);
  s.setProperty('--primary-hover', c.hover);
  s.setProperty('--primary-text', c.text);
  s.setProperty('--primary-tint', c.tint);
  // The high band of the relevance meter is the accent by design — it is the
  // same "this is good" signal, and leaving it green under a plum accent looks
  // like a bug rather than a choice.
  s.setProperty('--score-high', c.primary);

  try { localStorage.setItem(ACCENT_KEY, accentKey); } catch { /* ignore */ }
  emit();
}

/**
 * @param {'light'|'dark'|'system'} pref
 */
export function applyTheme(pref) {
  const mode = resolveMode(pref);
  document.documentElement.setAttribute('data-theme', mode);
  try { localStorage.setItem(THEME_KEY, pref); } catch { /* ignore */ }
  // Re-paint: the accent has different values per mode, so switching light to
  // dark without this leaves the dark UI wearing light-mode accent colours.
  // applyAccent emits, so every subscriber hears about both changes at once.
  applyAccent(currentAccent(), mode);
}

/** The user's stated preference, which may be 'system'. */
export function currentThemePref() {
  try { return localStorage.getItem(THEME_KEY) || 'system'; } catch { return 'system'; }
}

/** The mode actually in force right now. */
export function currentTheme() {
  return document.documentElement.getAttribute('data-theme') || 'light';
}

export function currentAccent() {
  try { return localStorage.getItem(ACCENT_KEY) || DEFAULT_ACCENT; } catch { return DEFAULT_ACCENT; }
}

/**
 * Has this device been told explicitly, or is it just showing the default?
 *
 * currentThemePref() answers 'system' both for "the user chose Match device"
 * and for "nobody has ever said", and currentAccent() answers 'forest' both for
 * "chose forest" and "never chose". syncThemeFromProfile has to tell those
 * apart, so it reads the raw slot instead.
 */
function storedRaw(key) {
  try { return localStorage.getItem(key); } catch { return null; }
}

/**
 * Apply what the account says, if it disagrees with this device.
 *
 * localStorage paints instantly on first byte — that is why the bootstrap in
 * index.html exists and why it must stay. But it is per-origin and per-device,
 * so on its own the preference is lost on a second device, in a private window,
 * and in any browser configured to refuse site storage (which is what makes
 * this look like "dark mode does not stick" rather than "dark mode is only on
 * this machine").
 *
 * So the account is the source of truth and localStorage is a cache of it. This
 * runs when the profile arrives: if they disagree, the account wins and the
 * cache is corrected, so the next first paint is already right.
 */
export function syncThemeFromProfile(profile) {
  if (!profile) return null;

  /**
   * THE DEVICE IN FRONT OF YOU WINS. The account is the default for a device
   * that has not been told.
   *
   * This used to be the other way round — every profile snapshot applied the
   * account's value over whatever this browser had — and that is why dark mode
   * "removed itself on refresh". The sequence was:
   *
   *   1. The inline bootstrap in index.html reads localStorage and paints dark.
   *      Correct, and before first paint, exactly as intended.
   *   2. React mounts, the profile arrives, and it says themePref: 'system'.
   *   3. 'system' !== 'dark', so applyTheme('system') ran, resolved against a
   *      light OS, and turned the page white.
   *
   * The two only have to disagree once for that to happen on every load
   * afterwards, and they disagree easily: the profile write behind the toggle
   * is fire-and-forget with a swallowed error, so a single failed write leaves
   * the account permanently stale and permanently winning.
   *
   * Now an explicit local choice is authoritative and the stale account value
   * is REPAIRED from it — returned as a patch for the caller to write, so the
   * failure heals itself instead of fighting the user on every page load.
   *
   * The cost is that changing the theme on your phone no longer repaints an
   * already-open desktop tab. It applies there on the next load instead. That
   * is a fair trade for the setting actually sticking.
   */
  const patch = {};

  const localPref = storedRaw(THEME_KEY);
  const hasLocalChoice = localPref === 'light' || localPref === 'dark' || localPref === 'system';
  const accountPref = profile.themePref;

  if (hasLocalChoice) {
    /**
     * Re-apply rather than assume. This is belt and braces, and the braces
     * have already failed once.
     *
     * The natural thing here is to do nothing — the boot script has already
     * painted from the same localStorage value, so re-applying looks like a
     * wasted call. It is not: for months that boot script was blocked by the
     * Content-Security-Policy and never ran at all, so "do nothing" meant
     * data-theme was never set by anybody and every dark-mode user got a white
     * page. applyTheme is idempotent and costs one attribute write, so it runs
     * whenever the DOM does not already agree.
     */
    if (resolveMode(localPref) !== currentTheme()) applyTheme(localPref);
    if (accountPref !== localPref) patch.themePref = localPref;
  } else if (accountPref && accountPref !== currentThemePref()) {
    applyTheme(accountPref);
  }

  // Same rule for the accent. applyAccent takes its mode from the DOM, so it
  // cannot disagree with whatever the branch above just settled.
  const localAccent = storedRaw(ACCENT_KEY);
  const accountAccent = profile.accent;

  if (localAccent) {
    if (accountAccent !== localAccent) patch.accent = localAccent;
  } else if (accountAccent && accountAccent !== currentAccent()) {
    applyAccent(accountAccent);
  }

  return Object.keys(patch).length ? patch : null;
}

/**
 * Follow the OS while the preference is 'system'.
 *
 * Without this, someone on automatic light/dark scheduling picks 'system' once
 * and then sits in whichever mode was current at that moment until they reload
 * — which reads as the setting not working.
 */
export function watchSystemTheme() {
  const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
  if (!mq) return () => {};
  const onChange = () => {
    if (currentThemePref() === 'system') applyTheme('system');
  };
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
}
